<template>
    <v-form v-model="isValid">
        <v-row v-if="caseType === CaseTypes.CASE_TYPE_NO_BOARDING.value">
            <v-col cols="12">
                <p>Warst du rechtzeitig am Check-In-Schalter und am Gate/Flugsteig?</p>
                <v-select v-model="formData.was_late_for_boarding" :items="wasLateForBoardingRadioValue" :rules="validationRules.selectField"/>
            </v-col>
            <v-col cols="12">
                <p>Wurde dir die Beförderung aufgrund von Krankheit oder Sicherheitsbedenken untersagt?</p>
                <v-select v-model="formData.was_boarding_lateness_acceptable" :items="selectStringYesNo" :rules="validationRules.selectField"/>
            </v-col>
        </v-row>
        <v-row v-else>
            <v-col cols="12">
                <p>Was war der Grund für die Flugproblematik?</p>
                <v-radio-group v-model="formData.cause" :rules="validationRules.defaultNotNull">
                    <v-radio v-for="causeType in CauseTypes.toArray()"
                             :key="causeType.value"
                             :value="causeType.value"
                             :label="causeType.title"/>
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <p>Wurdest Du vorab über die Flugzeitenänderung informiert?</p>
                <v-select v-model="viewData.wasInformedAboutChange" :items="selectYesNo" :rules="validationRules.selectField"/>
            </v-col>
            <v-col v-if="viewData.wasInformedAboutChange" cols="12">
                <p>Wann wurdest Du informiert?</p>
                <v-menu
                    ref="changeInformationDateMenu"
                    v-model="viewData.showChangeInformationDateMenu"
                    :close-on-content-click="false"
                    :return-value.sync="formData.change_notification_date"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                >
                    <template v-slot:activator="{on}">
                        <v-text-field
                            :value="formattedChangeNotificationDate"
                            readonly
                            v-on="on"
                            :rules="validationRules.defaultNotNull"
                        />
                    </template>
                    <v-date-picker
                        locale="de"
                        no-title
                        scrollable
                        v-model="formData.change_notification_date"
                        :max="viewData.maxFlightdate"
                    >
                        <v-spacer/>
                        <v-btn text color="secondary" @click="viewData.showChangeInformationDateMenu = false">Abbrechen</v-btn>
                        <v-btn text color="primary" @click="$refs.changeInformationDateMenu.save(formData.change_notification_date)">OK</v-btn>
                    </v-date-picker>
                </v-menu>
            </v-col>
        </v-row>
        <v-row v-if="hasStopover">
            <v-col cols="12">
                <v-col cols="12">
                    <p>Welcher Streckenabschnitt war tatsächlich betroffen?</p>
                    <v-select
                        v-model="formData.delayed_flight_type"
                        :items="delayedFlightTypeSelectData"
                        :rules="validationRules.selectField"
                        item-value="value"
                        item-text="title"
                    />
                </v-col>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <p>Hast Du eine Rechtsschutzversicherung?</p>
                <v-select v-model="viewData.hasRSV" :items="selectStringYesNo" :rules="validationRules.selectField"/>
            </v-col>
            <v-col cols="12" v-if="viewData.hasRSV === RadioButtonStringValues.YES.value">
                <p>
                    Bitte gib den Betrag deiner Selbstbeteiligung an oder setze ein Häkchen bei "Weiß ich nicht", falls
                    Dir diese Information aktuell nicht vorliegt. Wenn Du keine Selbstbeteiligung hast, trag bitte 0€ ein.
                </p>
                <v-currency-field
                    v-model="formData.rsv_deductible_amount"
                    :disabled="viewData.hasUnknownRSVDeductible"
                    prefix="€"
                    :rules="[isRSVDeductibleValid]"
                    ref="rsvDeductibleAmountField"
                />
                <v-checkbox
                    v-model="viewData.hasUnknownRSVDeductible"
                    label="Weiß ich nicht."
                    :rules="[isRSVDeductibleValid]"
                    ref="rsvDeductibleCheckbox"
                />
            </v-col>
        </v-row>
    </v-form>
</template>

<script>
import CauseTypes from "@/constants/CauseTypes";
import CaseTypes from "@/constants/CaseTypes";
import {isNotNull, requiresSelect} from "@/helper/ValidationHelper";
import RadioButtonStringValues from "@/constants/RadioButtonStringValues";
import RadioButtonBooleanValues from "@/constants/RadioButtonBooleanValues";
import {isNotNullOrUndefined} from "@/helper/TypeHelper";
import moment from "moment/moment";
import FlightTypes from "@/constants/FlightTypes";

export default {
    props: {
        caseType: {
            type: Number
        },
        hasStopover: {
            type: Boolean,
            default: false
        }
    },
    emits: ['validity-changed', 'form-data-changed', 'has-rsv-value-changed'],
    computed: {
        RadioButtonStringValues() {
            return RadioButtonStringValues
        },
        isRSVDeductibleValid()
        {
            if (this.formData.rsv_deductible_amount === null && !this.viewData.hasUnknownRSVDeductible)
                return 'Bitte gib die Höhe der Selbstbeteiligung an oder wähle "Weiß ich nicht." aus';

            return true;
        },
        CaseTypes() {
            return CaseTypes
        },
        CauseTypes() {
            return CauseTypes;
        },
        selectStringYesNo() {
            return [
                RadioButtonStringValues.YES,
                RadioButtonStringValues.NO
            ];
        },
        selectYesNo() {
            return [
                RadioButtonBooleanValues.YES,
                RadioButtonBooleanValues.NO
            ];
        },
        formattedChangeNotificationDate()
        {
            if (isNotNullOrUndefined(this.formData.change_notification_date))
                return moment(this.formData.change_notification_date).format('D.M.YYYY');

            return null;
        },
        wasLateForBoardingRadioValue()
        {
            // the values for this Radiobutton needs to be flipped, because the question in the UI has a different wording
            // in comparison to the database-field
            return [
                {
                    value: RadioButtonStringValues.NO.value,
                    text: RadioButtonStringValues.YES.text
                },
                {
                    value: RadioButtonStringValues.YES.value,
                    text: RadioButtonStringValues.NO.text
                }
            ]
        }
    },
    data: () => ({
        isValid: false,
        validationRules: {
            defaultNotNull: [
                isNotNull
            ],
            selectField: [
                requiresSelect
            ]
        },
        formData: {
            cause: null,
            was_late_for_boarding: null,
            was_boarding_lateness_acceptable: null,
            rsv_deductible_amount: null,
            change_notification_date: null,
            delayed_flight_type: null
        },
        viewData: {
            hasRSV: null,
            hasUnknownRSVDeductible: null,
            wasInformedAboutChange: null,
            showChangeInformationDateMenu: false
        },
        delayedFlightTypeSelectData: FlightTypes.toArray()
    }),
    watch: {
        caseType: function (newValue, oldValue)
        {
            if(oldValue === CaseTypes.CASE_TYPE_NO_BOARDING.value && newValue !== CaseTypes.CASE_TYPE_NO_BOARDING.value)
            {
                this.formData.was_boarding_lateness_acceptable = null;
                this.formData.was_late_for_boarding = null;
            }

            if(oldValue !== CaseTypes.CASE_TYPE_NO_BOARDING.value && newValue === CaseTypes.CASE_TYPE_NO_BOARDING.value)
                this.formData.cause = null;
        },
        'viewData.hasRSV': function (newValue)
        {
            if (newValue === RadioButtonStringValues.NO.value)
            {
                this.viewData.hasUnknownRSVDeductible = null;
                this.formData.rsv_deductible_amount = null;
            }

            this.$emit('has-rsv-value-changed', newValue);
        },
        'viewData.wasInformedAboutChange': function (newValue)
        {
            if (!newValue)
                this.formData.change_notification_date = null;
        },
        'viewData.hasUnknownRSVDeductible': function (newValue)
        {
            if (newValue)
                this.formData.rsv_deductible_amount = null;
        },
        'isValid': function (newValue)
        {
            this.$emit('validity-changed', newValue);
        },
        'hasStopover': function(newValue)
        {
            if (!newValue)
                this.formData.delayed_flight_type = null;
        },
        formData:
        {
            handler: function(formData)
            {
                this.$emit("form-data-changed", formData);
            },
            deep: true
        }
    }
}
</script>

<style scoped>

</style>
